import { CruisesContract as API } from '@luxuryescapes/contract-svc-cruise';

import {
  BundleFilter,
  BundleFilterCreate,
  BundleItem,
  BundleItemCreate,
  BundleItemOption,
  BundleItemOptionCreate,
  CruiseBundle,
  CruiseBundleCreate,
  CruiseBundleUpdate,
} from '~/components/Cruises/pages/Bundles/types';

import cruisesAPI from './CruisesApiService';

const RESOURCE = 'bundles';

// Bundles
const listWithPagination = async (
  paginationParams: API.PaginationParams & { name?: string },
): Promise<API.Response<Array<CruiseBundle>>> => {
  return await cruisesAPI.http.get({ resource: RESOURCE, paginationParams });
};

const create = async (formData: CruiseBundleCreate): Promise<CruiseBundle> => {
  const response = await cruisesAPI.http.post<API.Response<CruiseBundle>>({
    resource: RESOURCE,
    body: formData as unknown as Record<string, unknown>,
  });
  return response.result;
};

const update = async (id: string, formData: CruiseBundleUpdate): Promise<CruiseBundle> => {
  const response = await cruisesAPI.http.put<CruiseBundle>({
    resource: `${RESOURCE}/${id}`,
    body: formData as unknown as Record<string, unknown>,
  });
  return response.result;
};

const getById = async (id: string): Promise<CruiseBundle> => {
  const response = await cruisesAPI.http.get<CruiseBundle>({
    resource: `${RESOURCE}/${id}`,
  });
  return response.result;
};

// Bundle Items
const getItems = async (bundleId: string): Promise<API.Response<Array<BundleItem>>> => {
  return cruisesAPI.http.get({
    resource: `${RESOURCE}/${bundleId}/items`,
  });
};

const createItem = async (bundleId: string, formData: BundleItemCreate): Promise<BundleItem> => {
  const response = await cruisesAPI.http.post<API.Response<BundleItem>>({
    resource: `${RESOURCE}/${bundleId}/items`,
    body: formData,
  });
  return response.result;
};

const updateItem = async (bundleId: string, id: string, formData: Partial<BundleItemCreate>): Promise<BundleItem> => {
  const response = await cruisesAPI.http.put<BundleItem>({
    resource: `${RESOURCE}/${bundleId}/items/${id}`,
    body: formData,
  });
  return response.result;
};

const deleteItem = async (bundleId: string, id: string): Promise<void> => {
  await cruisesAPI.http.delete({
    resource: `${RESOURCE}/${bundleId}/items/${id}`,
  });
};

// Bundle Item Options
const getOptions = async (bundleId: string, itemId: string): Promise<API.Response<Array<BundleItemOption>>> => {
  return cruisesAPI.http.get({
    resource: `${RESOURCE}/${bundleId}/items/${itemId}/options`,
  });
};

const createOption = async (
  bundleId: string,
  itemId: string,
  formData: BundleItemOptionCreate,
): Promise<BundleItemOption> => {
  const response = await cruisesAPI.http.post<API.Response<BundleItemOption>>({
    resource: `${RESOURCE}/${bundleId}/items/${itemId}/options`,
    body: formData,
  });
  return response.result;
};

const updateOption = async (
  bundleId: string,
  itemId: string,
  optionId: string,
  formData: Partial<BundleItemOptionCreate>,
): Promise<BundleItemOption> => {
  const response = await cruisesAPI.http.put<BundleItemOption>({
    resource: `${RESOURCE}/${bundleId}/items/${itemId}/options/${optionId}`,
    body: formData,
  });
  return response.result;
};

const deleteOption = async (bundleId: string, itemId: string, optionId: string): Promise<void> => {
  await cruisesAPI.http.delete({
    resource: `${RESOURCE}/${bundleId}/items/${itemId}/options/${optionId}`,
  });
};

// Bundle Filters
const getFilters = async (bundleId: string): Promise<API.Response<Array<BundleFilter>>> => {
  return cruisesAPI.http.get({
    resource: `${RESOURCE}/${bundleId}/filters`,
  });
};

const createFilter = async (bundleId: string, formData: BundleFilterCreate): Promise<BundleFilter> => {
  const response = await cruisesAPI.http.post<API.Response<BundleFilter>>({
    resource: `${RESOURCE}/${bundleId}/filters`,
    body: formData,
  });
  return response.result;
};

const deleteFilter = async (bundleId: string, filterId: string): Promise<void> => {
  await cruisesAPI.http.delete({
    resource: `${RESOURCE}/${bundleId}/filters/${filterId}`,
  });
};

export default {
  listWithPagination,
  create,
  update,
  getById,
  // Items
  getItems,
  createItem,
  updateItem,
  deleteItem,
  // Options
  getOptions,
  createOption,
  updateOption,
  deleteOption,
  // Filters
  getFilters,
  createFilter,
  deleteFilter,
};
